/** App chrome: navigation, rail, assistant panel, tabs, keyboard shortcuts, loader. */
import { $$, byId, on, toast } from "../lib/dom";
import { createOrb, type Orb } from "./orb";

export type PageId = "today" | "calendar" | "tasks" | "meetings" | "notes" | "research" | "inbox" | "focus" | "booking" | "extension" | "integrations" | "analytics" | "settings" | "more" | "admin";

const listeners = new Set<(page: PageId) => void>();
export const onPageChange = (fn: (page: PageId) => void) => listeners.add(fn);

export function goTo(page: PageId) {
  // The AI inbox is a section of Tasks now. Anything that still asks for it - an old link, a
  // notification, a bookmark - lands where its contents went.
  if (page === "inbox") page = "tasks" as PageId;
  $$(".page").forEach((p) => p.classList.toggle("active", p.id === `page-${page}`));
  $$("#nav button, .advanced-nav button").forEach((b) => b.classList.toggle("active", b.dataset.page === page));
  history.replaceState(null, "", `#${page}`);
  window.scrollTo({ top: 0 });
  listeners.forEach((fn) => fn(page));
}

export function currentPage(): PageId {
  return ((location.hash.replace("#", "") as PageId) || "today");
}

export function setupShell() {
  const appShell = byId("appShell")!;

  // Rail
  const railToggle = byId("railToggle")!;
  const setRail = (collapsed: boolean) => {
    appShell.classList.toggle("rail-collapsed", collapsed);
    railToggle.setAttribute("aria-expanded", String(!collapsed));
    localStorage.setItem("timo-sidebar-collapsed", String(collapsed));
  };
  setRail(localStorage.getItem("timo-sidebar-collapsed") === "true");
  on(railToggle, "click", () => setRail(!appShell.classList.contains("rail-collapsed")));

  // Meeting assistant panel
  const assistant = byId("meetingAssistant")!;
  const assistantToggle = byId("assistantGlobalToggle")!;
  const setAssistant = (open: boolean) => {
    appShell.classList.toggle("assistant-collapsed", !open);
    assistant.classList.toggle("is-collapsed", !open);
    assistant.classList.toggle("expanded", open && window.innerWidth <= 930);
    assistantToggle.classList.toggle("active", open);
    localStorage.setItem("timo-assistant-open-v3", String(open));
  };
  // Open by default on wide screens; the user's choice is remembered once they toggle it.
  // On a phone the panel is a full-screen sheet, so it always starts closed: a remembered "open"
  // from a desktop session used to cover every page and swallow every tap.
  const saved = localStorage.getItem("timo-assistant-open-v3");
  const narrow = () => window.innerWidth <= 930;
  setAssistant(narrow() ? false : saved === null ? true : saved === "true");
  on(assistantToggle, "click", () => setAssistant(assistant.classList.contains("is-collapsed")));
  on(byId("openAssistantPeek"), "click", () => setAssistant(true));
  on(byId("collapseAssistant"), "click", () => setAssistant(false));
  on(byId("expandMeeting"), "click", () => assistant.classList.toggle("expanded"));
  document.addEventListener("timo:assistant", ((e: CustomEvent<boolean>) => setAssistant(e.detail)) as EventListener);

  // Page navigation (any element with data-page)
  document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-page]");
    if (btn?.dataset.page) goTo(btn.dataset.page as PageId);
  });
  // Going somewhere closes the phone sheet, so the page you asked for is the page you see
  onPageChange(() => { if (narrow() && !assistant.classList.contains("is-collapsed")) setAssistant(false); });
  $$("#nav button").forEach((b) => (b.title = b.querySelector("span:not(.ico):not(.count)")?.textContent?.trim() ?? ""));

  // Tabs inside the assistant
  $$(".tab").forEach((tab) =>
    on(tab, "click", () => {
      $$(".tab").forEach((t) => t.classList.remove("active"));
      $$(".tab-content").forEach((t) => t.classList.remove("active"));
      tab.classList.add("active");
      byId(`tab-${tab.dataset.tab}`)?.classList.add("active");
    }),
  );
  $$("[data-tab-jump]").forEach((b) => on(b, "click", () => (document.querySelector<HTMLElement>(`.tab[data-tab="${b.dataset.tabJump}"]`))?.click()));

  // Segmented controls and simple toasts from the design
  $$(".segmented").forEach((g) => $$("button", g).forEach((b) => on(b, "click", () => { $$("button", g).forEach((x) => x.classList.remove("active")); b.classList.add("active"); })));
  // There used to be a global handler here that made any element with data-toast announce success
  // on click. They were left over from the static mock, and a button that says "Draft scheduled"
  // without scheduling anything teaches people that nothing in the app can be trusted. The
  // attribute is gone from the markup; the handler goes with it, so nothing can claim that again.

  // Keyboard. Single keys only fire when you are not typing and no dialog is open, six that earn their place.
  const typing = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
  };
  const dialogOpen = () => !!document.querySelector(".modal-layer.show, #onboarding.show");
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); byId<HTMLInputElement>("commandInput")?.focus(); }
    if ((e.metaKey || e.ctrlKey) && e.key === "\\") { e.preventDefault(); setRail(!appShell.classList.contains("rail-collapsed")); }
    if (e.key === "Escape") { document.dispatchEvent(new CustomEvent("timo:escape")); byId("shortcutSheet")?.classList.remove("show"); }
    if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
    if (e.key === "/") { e.preventDefault(); byId<HTMLInputElement>("commandInput")?.focus(); return; }
    if (e.key === "?") { e.preventDefault(); byId("shortcutSheet")?.classList.toggle("show"); return; }
    if (dialogOpen()) return;
    const k = e.key.toLowerCase();
    if (k === "r") { e.preventDefault(); document.dispatchEvent(new CustomEvent("timo:record-toggle")); }
    else if (k === "m") { e.preventDefault(); document.dispatchEvent(new CustomEvent("timo:mute-toggle")); }
    else if (k === "c") { e.preventDefault(); document.dispatchEvent(new CustomEvent("timo:create", { detail: "task" })); }
    else if (k === "a") { e.preventDefault(); assistantToggle.click(); }
  });

  // Press ? for the list. The only place shortcuts are documented, so it stays honest.
  document.body.insertAdjacentHTML("beforeend", `<div class="shortcut-sheet" id="shortcutSheet"><b>Keyboard</b><dl>
    <dt>⌘K</dt><dd>Command bar</dd>
    <dt>/</dt><dd>Search and capture</dd>
    <dt>R</dt><dd>Start / stop recording</dd>
    <dt>M</dt><dd>Mute while recording</dd>
    <dt>C</dt><dd>Create a task</dd>
    <dt>A</dt><dd>Meeting notes panel</dd>
    <dt>Esc</dt><dd>Close</dd></dl></div>`);

  // Command bar (V1: routes to create-task; NL scheduling is a fast-follow)
  on(byId("commandForm"), "submit", (e) => {
    e.preventDefault();
    const input = byId<HTMLInputElement>("commandInput")!;
    const text = input.value.trim();
    if (!text) return;
    document.dispatchEvent(new CustomEvent("timo:command", { detail: text }));
    input.value = "";
  });
  // Voice button: browser SpeechRecognition where available (see dictateInto for the shared plumbing)
  const voiceBtn = byId("voiceBtn");
  const voiceHint = byId("voiceHint");
  let voiceOrb: Orb | null = null;
  const showVoiceOrb = (on: boolean) => { if (on && !voiceOrb) { byId("commandForm")!.insertAdjacentHTML("afterend", `<div class="voice-orb" id="voiceOrb"></div>`); voiceOrb = createOrb(byId("voiceOrb")!, 38); voiceOrb.setState("listening"); } if (!on && voiceOrb) { voiceOrb.destroy(); byId("voiceOrb")?.remove(); voiceOrb = null; } };
  on(voiceBtn, "click", () => {
    type Rec = { lang: string; start(): void; onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null; onend: (() => void) | null };
    const SR = window as unknown as { webkitSpeechRecognition?: new () => Rec; SpeechRecognition?: new () => Rec };
    const Ctor = SR.SpeechRecognition ?? SR.webkitSpeechRecognition;
    if (!Ctor) return toast("Voice input isn't available in this browser");
    const rec = new Ctor();
    rec.lang = navigator.language;
    voiceBtn!.classList.add("listening"); voiceHint?.classList.add("show"); showVoiceOrb(true);
    let heard = "";
    rec.onresult = (ev) => { heard = ev.results[0]?.[0]?.transcript ?? ""; byId<HTMLInputElement>("commandInput")!.value = heard; };
    rec.onend = () => { showVoiceOrb(false); voiceBtn!.classList.remove("listening"); voiceHint?.classList.remove("show"); if (heard.trim()) { toast(`Heard: ${heard}`); document.dispatchEvent(new CustomEvent("timo:voice", { detail: heard })); } };
    rec.start();
  });

  // Agent banner
  const banner = byId("agentConnectBanner");
  if (localStorage.getItem("timo-agent-banner-dismissed") === "true") banner?.classList.add("hidden");
  on(byId("agentBannerDismiss"), "click", () => { banner?.classList.add("hidden"); localStorage.setItem("timo-agent-banner-dismissed", "true"); });

  // The phone layout leans on this: recording takes the screen, the bar orb turns red
  // The bar lives outside .app, so the flag goes on <body> where both can see it
  document.addEventListener("timo:recording-state", ((e: CustomEvent<boolean>) => { appShell.classList.toggle("is-recording", !!e.detail); document.body.classList.toggle("is-recording", !!e.detail); const lbl = document.querySelector(".bar-record-label"); if (lbl) lbl.textContent = e.detail ? "Live" : "Record"; }) as EventListener);

  goTo(currentPage());
}

type Rec = { lang: string; start(): void; stop(): void; onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null; onend: (() => void) | null };
/** Dictate into any input. Returns false when the browser has no speech recognition (Safari/Firefox). */
export function dictateInto(input: HTMLInputElement, btn: HTMLElement, submit = false) {
  const SR = window as unknown as { webkitSpeechRecognition?: new () => Rec; SpeechRecognition?: new () => Rec };
  const Ctor = SR.SpeechRecognition ?? SR.webkitSpeechRecognition;
  if (!Ctor) { toast("Voice input isn't available in this browser"); return false; }
  if (btn.classList.contains("listening")) return true; // already listening
  const rec = new Ctor();
  rec.lang = navigator.language;
  btn.classList.add("listening");
  let heard = "";
  rec.onresult = (ev) => { heard = ev.results[0]?.[0]?.transcript ?? ""; input.value = heard; };
  rec.onend = () => { btn.classList.remove("listening"); if (heard.trim() && submit) input.form?.requestSubmit(); else input.focus(); };
  rec.start();
  return true;
}

export function setLoader(status: string, done = false) {
  const el = byId("loaderStatus");
  if (el) el.textContent = status;
  if (done) byId("appLoader")?.classList.add("done");
}

export function setProfile(name: string, sub = "Personal workspace") {
  $$(".avatar").forEach((a) => (a.textContent = (name[0] ?? "T").toUpperCase()));
  const copy = document.querySelector(".profile-copy");
  if (copy) copy.innerHTML = `<b></b><small></small>`;
  const b = copy?.querySelector("b"), s = copy?.querySelector("small");
  if (b) b.textContent = name;
  if (s) s.textContent = sub;
}
